import { Link } from 'react-router-dom';
import { Sparkles, Wand2, Globe, ShieldCheck } from 'lucide-react';

const MONTSERRAT = { fontFamily: 'Montserrat, sans-serif' } as const;

/**
 * AuthShell — the split "stage" layout for the auth funnel (login / register /
 * onboarding). The premium alternative to a card floating on a gradient: a deep
 * plum brand stage (left) that states the product promise + quiet proof points,
 * paired with the form (right) on a clean surface. On mobile the stage collapses
 * to a compact branded header above the form.
 *
 * The stage copy is intentionally fixed brand voice — only the form header
 * (title/subtitle), the form itself (children) and the bottom switch link change
 * between screens.
 */
export interface AuthShellProps {
  title: string;
  subtitle: string;
  children: React.ReactNode;
  /** Bottom switch line, e.g. "Don't have an account?" + "Create one free". */
  altPrompt?: string;
  altCta?: string;
  altTo?: string;
}

const PROOF = [
  { icon: Wand2, label: 'Describe it — the AI builds it', hint: 'No developer, no templates to wrestle' },
  { icon: Globe, label: 'Live on your own domain in minutes', hint: 'MLS-connected, mobile, fast' },
  { icon: ShieldCheck, label: 'Your brand, your leads', hint: 'Forms go straight to your inbox' },
];

function BrandLockup({ className = '' }: { className?: string }) {
  return (
    <div className={`flex items-center gap-2.5 ${className}`}>
      <span
        className="flex h-9 w-9 items-center justify-center rounded-xl text-sm font-bold ring-1 ring-white/15"
        style={{
          background: 'linear-gradient(135deg, #C9B8F4 0%, #E6D9FC 100%)',
          color: '#10032A',
          boxShadow: '0 6px 22px rgba(201,184,244,0.4)',
          ...MONTSERRAT,
        }}
      >
        BL
      </span>
      <span className="text-sm font-semibold tracking-tight text-white" style={MONTSERRAT}>
        Bread List Sites
      </span>
    </div>
  );
}

export default function AuthShell({ title, subtitle, children, altPrompt, altCta, altTo }: AuthShellProps) {
  return (
    <div className="grid min-h-screen lg:grid-cols-[1.05fr_1fr]">
      {/* ── Brand stage (desktop) ──────────────────────────────────────────── */}
      <aside
        className="relative hidden overflow-hidden lg:flex lg:flex-col lg:justify-between lg:p-12 xl:p-14"
        style={{ background: 'linear-gradient(155deg, #10032A 0%, #190733 55%, #2A0B4A 100%)' }}
      >
        {/* Ambient depth — layered glows + a faint dot grid. Decorative, aria-hidden. */}
        <div aria-hidden className="pointer-events-none absolute inset-0">
          <div
            className="auth-glow absolute -left-24 top-[-10%] h-[480px] w-[480px] rounded-full opacity-60 blur-3xl"
            style={{ background: 'radial-gradient(circle, rgba(201,184,244,0.32), transparent 62%)' }}
          />
          <div
            className="auth-glow-slow absolute -right-32 bottom-[-12%] h-[520px] w-[520px] rounded-full opacity-50 blur-3xl"
            style={{ background: 'radial-gradient(circle, rgba(78,31,96,0.85), transparent 60%)' }}
          />
          <div
            className="absolute inset-0 opacity-[0.07]"
            style={{
              backgroundImage: 'radial-gradient(rgba(201,184,244,0.9) 1px, transparent 1px)',
              backgroundSize: '22px 22px',
            }}
          />
        </div>

        <div className="relative">
          <BrandLockup />
        </div>

        <div className="relative max-w-md">
          <span
            className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1 text-xs font-medium text-brand-lavender ring-1 ring-white/10"
            style={MONTSERRAT}
          >
            <Sparkles className="h-3.5 w-3.5" />
            AI website builder for real estate
          </span>
          <h2
            className="mt-6 text-balance text-4xl font-semibold leading-[1.08] tracking-[-0.02em] text-white xl:text-[2.75rem]"
            style={MONTSERRAT}
          >
            Your real estate website, built by AI.
          </h2>
          <p className="mt-4 max-w-sm text-[15px] leading-relaxed text-brand-lavender/85">
            Describe what you want. Watch it come together. Go live on your own domain — no
            developer, no code, no waiting.
          </p>

          <ul className="mt-9 space-y-4">
            {PROOF.map(({ icon: Icon, label, hint }) => (
              <li key={label} className="flex items-start gap-3.5">
                <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white/[0.08] text-brand-lavender ring-1 ring-white/10">
                  <Icon className="h-4 w-4" />
                </span>
                <span>
                  <span className="block text-sm font-medium text-white">{label}</span>
                  <span className="block text-[13px] text-brand-lavender/70">{hint}</span>
                </span>
              </li>
            ))}
          </ul>
        </div>

        <p className="relative text-xs text-brand-lavender/55">
          Built for real estate professionals · Simon Gray Realty &amp; the Bread List suite
        </p>
      </aside>

      {/* ── Form column ────────────────────────────────────────────────────── */}
      <main className="flex flex-col items-center justify-center bg-brand-surface px-6 py-12 sm:px-10">
        <div className="auth-rise w-full max-w-[24rem]">
          {/* Compact brand header — mobile only (stage is hidden < lg) */}
          <div className="mb-8 lg:hidden">
            <span
              className="flex h-11 w-11 items-center justify-center rounded-xl text-base font-bold ring-1 ring-brand-border"
              style={{
                background: 'linear-gradient(135deg, #4E1F60 0%, #10032A 100%)',
                color: '#E6D9FC',
                boxShadow: '0 8px 24px rgba(16,3,42,0.18)',
                ...MONTSERRAT,
              }}
            >
              BL
            </span>
          </div>

          <header className="mb-7">
            <h1 className="text-[1.75rem] font-semibold tracking-tight text-brand-midnight" style={MONTSERRAT}>
              {title}
            </h1>
            <p className="mt-1.5 text-sm text-brand-ink">{subtitle}</p>
          </header>

          {children}

          {altPrompt && altCta && altTo && (
            <p className="mt-8 text-center text-sm text-brand-ink">
              {altPrompt}{' '}
              <Link to={altTo} className="font-semibold text-brand-plum hover:underline">
                {altCta}
              </Link>
            </p>
          )}
        </div>
      </main>
    </div>
  );
}
